import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Elements } from '@stripe/react-stripe-js';

import client from '../api/client';
import { stripePromise, stripeAppearance } from '../api/stripe';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import OrderStatusBadge from '../components/order/OrderStatusBadge';
import BalancePaymentStep from '../components/order/BalancePaymentStep';
import { formatPrice } from '../utils/money';
import { parseApiError } from '../utils/apiError';

const POLL_MS = 3000;
const MAX_BALANCE_POLLS = 10;

const CANNOT_PAY_BALANCE_STATUSES = ['cancelled', 'refunded'];

function AddressBlock({ label, address }) {
  if (!address) return null;
  return (
    <div className="ss-address-block">
      <span className="ss-label">{label}</span>
      {address.fullName}<br />
      {address.line1}{address.line2 ? `, ${address.line2}` : ''}<br />
      {address.city}, {address.state} {address.postalCode}<br />
      {address.country}
      {address.phone ? <><br />{address.phone}</> : null}
    </div>
  );
}

export default function OrderDetail() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ship/Pickups link here with location state naming where they came from,
  // so the back link returns there instead of always assuming /orders.
  const backTo = location.state?.from || '/orders';
  const backLabel = location.state?.fromLabel || 'orders';

  // "Pay remaining balance" flow — mirrors checkout's PaymentStep, but stays
  // on this page and polls the order until the webhook clears balanceDue.
  const [balanceIntent, setBalanceIntent] = useState(null); // { clientSecret, amount }
  const [startingBalance, setStartingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState(null);
  const [balancePolling, setBalancePolling] = useState(false);

  // Admin-only pickup-lifecycle actions for local-pickup orders:
  // processing -> ready_for_pickup (staged, waiting on customer) -> picked_up.
  const [markingPickedUp, setMarkingPickedUp] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [pickupError, setPickupError] = useState(null);

  const fetchOrder = useCallback(async () => {
    const { data } = await client.get(`/order/${orderNumber}`);
    setOrder(data);
    return data;
  }, [orderNumber]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchOrder()
      .catch((err) => {
        if (active && err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchOrder]);

  const startBalancePayment = async () => {
    if (!user || String(order?.user) !== String(user.id)) return;
    setStartingBalance(true);
    setBalanceError(null);
    try {
      const { data } = await client.post(`/order/${orderNumber}/balance-intent`);
      setBalanceIntent(data);
    } catch (err) {
      setBalanceError(parseApiError(err).formError || 'Could not start payment.');
    } finally {
      setStartingBalance(false);
    }
  };

  // The client never marks the balance paid itself — poll until the Stripe
  // webhook flips balanceDue to 0, same pattern as OrderConfirmation.
  const onBalancePaid = () => {
    setBalanceIntent(null);
    setBalancePolling(true);

    let tries = 0;
    const poll = async () => {
      const data = await fetchOrder().catch(() => null);
      tries += 1;
      if (!data || data.balanceDue === 0 || tries >= MAX_BALANCE_POLLS) {
        setBalancePolling(false);
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
  };

  const markPickedUp = async () => {
    setMarkingPickedUp(true);
    setPickupError(null);
    try {
      await client.put(`/admin/orders/${order._id}/status`, { status: 'picked_up' });
      await fetchOrder();
    } catch (err) {
      setPickupError(parseApiError(err).formError || 'Could not mark picked up.');
    } finally {
      setMarkingPickedUp(false);
    }
  };

  const markReadyForPickup = async () => {
    setMarkingReady(true);
    setPickupError(null);
    try {
      await client.put(`/admin/orders/${order._id}/status`, { status: 'ready_for_pickup' });
      await fetchOrder();
    } catch (err) {
      setPickupError(parseApiError(err).formError || 'Could not mark ready for pickup.');
    } finally {
      setMarkingReady(false);
    }
  };

  // Admin-only soft-delete. The order disappears from every list/lookup once
  // deleted, so there's nothing left on this page to show — navigate back to
  // wherever the admin came from (restorable later via Deleted orders).
  const onDeleteOrder = async () => {
    const ok = await confirm({
      title: 'Delete order',
      message: `Delete order ${order.orderNumber}? It can be restored later from Deleted orders.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await client.delete(`/admin/orders/${order._id}`);
      addToast(`Order ${order.orderNumber} deleted`, { variant: 'success' });
      navigate(backTo);
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not delete order', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="ss-container ss-state"><Spinner label="Loading order" size={32} /></div>;
  }
  if (notFound || !order) {
    return (
      <section className="ss-container ss-state">
        <h2>Order not found</h2>
        <p style={{ marginTop: 'var(--ss-space-4)' }}>
          <Link className="ss-btn ss-btn--primary" to={backTo}>Back to {backLabel}</Link>
        </p>
      </section>
    );
  }

  // Paying the balance is owner-only, even for admins viewing someone else's
  // order — the server rejects it too (see createBalanceIntent), this just
  // keeps the button from being offered in the first place.
  const isOwner = !!user && String(order.user) === String(user.id);

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)', maxWidth: 720 }}>
      <p style={{ marginBottom: 'var(--ss-space-4)' }}>
        <Link to={backTo} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={16} aria-hidden="true" /> Back to {backLabel}
        </Link>
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-3)', flexWrap: 'wrap' }}>
        <h1>{order.orderNumber}</h1>
        <OrderStatusBadge status={order.status} />
        <span style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem' }}>Payment:</span>
        <OrderStatusBadge status={order.paymentStatus} />
        {isAdmin && (
          <button
            type="button"
            className="ss-icon-btn ss-icon-btn--danger"
            onClick={onDeleteOrder}
            disabled={deleting}
            aria-label={`Delete order ${order.orderNumber}`}
            style={{ marginLeft: 'auto' }}
          >
            {deleting ? <Spinner label="Deleting" /> : <Trash2 size={16} aria-hidden="true" />}
          </button>
        )}
      </div>

      <Card style={{ marginTop: 'var(--ss-space-4)' }}>
        {(order.items || []).map((i) => (
          <div key={i.product} className="ss-summary-line">
            <span>
              {i.title} × {i.quantity}
              <Badge className={i.fulfillment === 'pickup' ? 'ss-pickup-badge' : 'ss-ship-badge'}>
                {i.fulfillment === 'pickup' ? 'Pickup' : 'Ship'}
              </Badge>
              {i.depositRequired && <Badge className="ss-deposit-badge">{i.depositPercent}% deposit</Badge>}
            </span>
            <span>{formatPrice(i.subTotal ?? i.unitPrice * i.quantity)}</span>
          </div>
        ))}
        <div className="ss-summary-line ss-summary-line--muted" style={{ marginTop: 'var(--ss-space-2)' }}>
          <span>Items</span>
          <span>{formatPrice(order.itemTotal)}</span>
        </div>
        {order.shipping?.method && (
          <div className="ss-summary-line ss-summary-line--muted">
            <span>Shipping ({order.shipping.method})</span>
            <span>{formatPrice(order.shipping.cost)}</span>
          </div>
        )}
        {order.depositTotal > 0 ? (
          <>
            <div className="ss-summary-line ss-summary-line--muted">
              <span>Order total</span>
              <span>{formatPrice(order.total)}</span>
            </div>
            <div className="ss-summary-total">
              <span>Paid at checkout</span>
              <span>{formatPrice(order.amountDueAtCheckout)}</span>
            </div>
          </>
        ) : (
          <div className="ss-summary-total">
            <span>Total</span>
            <span>{formatPrice(order.total)}</span>
          </div>
        )}
      </Card>

      {order.depositTotal > 0 && (
        <Card style={{ marginTop: 'var(--ss-space-4)' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: 'var(--ss-space-3)' }}>Balance</h2>

          {order.balanceDue === 0 ? (
            <p>
              {order.balancePaidAt
                ? `Balance paid in full on ${new Date(order.balancePaidAt).toLocaleDateString()}.`
                : 'No balance due.'}
            </p>
          ) : !isOwner ? (
            <p className="ss-balance-due-amount">Balance due: {formatPrice(order.balanceDue)}</p>
          ) : balanceIntent ? (
            stripePromise ? (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: balanceIntent.clientSecret, appearance: stripeAppearance }}
              >
                <BalancePaymentStep
                  amount={balanceIntent.amount}
                  orderNumber={order.orderNumber}
                  onPaid={onBalancePaid}
                  onCancel={() => setBalanceIntent(null)}
                />
              </Elements>
            ) : (
              <p className="ss-form__error">Payments are not configured (missing Stripe key).</p>
            )
          ) : balancePolling ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)' }}>
              <Spinner label="Confirming payment" />
              <span>Confirming your payment…</span>
            </div>
          ) : order.paymentStatus === 'balance_due' &&
            !CANNOT_PAY_BALANCE_STATUSES.includes(order.status) ? (
            <>
              <p>You still owe <span className="ss-balance-due-amount">{formatPrice(order.balanceDue)}</span> on this order.</p>
              {balanceError && <p className="ss-form__error" role="alert">{balanceError}</p>}
              <Button
                onClick={startBalancePayment}
                disabled={startingBalance}
                style={{ marginTop: 'var(--ss-space-3)' }}
              >
                {startingBalance ? (
                  <Spinner label="Preparing payment" />
                ) : (
                  `Pay remaining balance (${formatPrice(order.balanceDue)})`
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="ss-balance-due-amount">Balance due: {formatPrice(order.balanceDue)}</p>
              <p>This becomes payable once the deposit payment is confirmed.</p>
            </>
          )}
        </Card>
      )}

      {order.requiresPickup && (
        <Card style={{ marginTop: 'var(--ss-space-4)' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: 'var(--ss-space-3)' }}>Pickup</h2>

          {order.pickedUpAt ? (
            <p>Picked up on {new Date(order.pickedUpAt).toLocaleDateString()}.</p>
          ) : isAdmin ? (
            <>
              <p>
                {order.status === 'ready_for_pickup'
                  ? 'This order is staged and ready for pickup.'
                  : 'This order is awaiting pickup.'}
              </p>
              {pickupError && <p className="ss-form__error" role="alert">{pickupError}</p>}
              <div style={{ display: 'flex', gap: 'var(--ss-space-3)', marginTop: 'var(--ss-space-3)' }}>
                {order.status !== 'ready_for_pickup' && (
                  <Button
                    variant="ghost"
                    onClick={markReadyForPickup}
                    disabled={markingReady || markingPickedUp}
                  >
                    {markingReady ? <Spinner label="Marking" /> : 'Mark ready for pickup'}
                  </Button>
                )}
                <Button
                  onClick={markPickedUp}
                  disabled={markingPickedUp || markingReady}
                >
                  {markingPickedUp ? <Spinner label="Marking" /> : 'Mark picked up'}
                </Button>
              </div>
            </>
          ) : (
            <p>
              {order.status === 'ready_for_pickup'
                ? 'Your order is ready for pickup!'
                : "This order includes an item for local pickup — we'll let you know when it's ready."}
            </p>
          )}
        </Card>
      )}

      <div className="ss-order-grid" style={{ marginTop: 'var(--ss-space-4)' }}>
        {order.shippingAddress && (
          <Card><AddressBlock label="Shipping to" address={order.shippingAddress} /></Card>
        )}
        <Card><AddressBlock label="Billing to" address={order.billingAddress} /></Card>
      </div>

      {dialog}
    </section>
  );
}
